import React, { useMemo } from 'react';
import { CircleMarker } from 'react-leaflet';
import { useSelector } from 'react-redux';

import InteractiveMap, {
  ESRI_SATELLITE_TILE_URL,
  ESRI_SATELLITE_TILE_ATTRIBUTION,
} from 'components/InteractiveMap';
import CanvasHeatLayer from 'components/InteractiveMap/CanvasHeatLayer';
import Icon from 'components/DesignSystem/Icon';
import {
  AgroOverviewLocation,
  AgroOverviewMapVariable,
  MapValuePoint,
  RiskLevel,
} from 'types/Meteorology/Agro/Overview/Dashboard';
import {
  RISK_COLOR,
  removerSufixoUf,
} from 'helper/Meteorology/Agro/Overview/locationsTable';
import {
  colorScaleForVariable,
  derivedNeutralHeatConfig,
  isLightScaleColor,
  scaleStopLabel,
} from 'helper/Meteorology/Agro/Overview/colorScale';
import {
  selectAgroOverviewLayers,
  selectAgroOverviewSelectedVariable,
} from 'redux/reducers/meteorologyAgroOverviewDashboardReducer';
import { cssVar } from 'constants/theme';
import BrazilStatesLayer from 'components/Meteorology/Agro/BrazilStatesLayer';
import BrazilCountryLayer from '../../../BrazilCountryLayer';

import './index.scss';

export default function FullscreenMap({
  open,
  dismiss,
  locations,
  valuePoints,
  variables,
}: {
  open: boolean;
  dismiss: () => void;
  locations: AgroOverviewLocation[];
  valuePoints: MapValuePoint[];
  variables: AgroOverviewMapVariable[];
}) {
  const selectedVariable = useSelector(selectAgroOverviewSelectedVariable);
  const layers = useSelector(selectAgroOverviewLayers);

  const selectedVariableMeta = variables.find((v) => v.nome === selectedVariable);
  const unidade = selectedVariableMeta?.unidade ?? '';

  const scale = useMemo(
    () => colorScaleForVariable(
      selectedVariable,
      valuePoints.map((p) => p.valor),
      selectedVariableMeta?.config,
    ),
    [selectedVariable, valuePoints, selectedVariableMeta],
  );

  const heatPoints = useMemo(
    () => valuePoints.map(
      (p) => [p.latitude, p.longitude, p.valor] as [number, number, number],
    ),
    [valuePoints],
  );
  const heatConfig = useMemo(
    () => selectedVariableMeta?.config
      ?? derivedNeutralHeatConfig(valuePoints.map((p) => p.valor))
      ?? undefined,
    [selectedVariableMeta, valuePoints],
  );

  if (!open) return null;

  return (
    <div className="agro-overview-mobile-fullscreen">
      <div className="agro-overview-mobile-fullscreen-topbar">
        <span className="agro-overview-mobile-fullscreen-title">
          {selectedVariableMeta?.label ?? 'Mapa'}
        </span>
        <button
          type="button"
          className="agro-overview-mobile-fullscreen-close"
          onClick={dismiss}
          aria-label="Fechar"
        >
          <Icon iconType="close" color={cssVar('text-strong')} size="small" />
        </button>
      </div>

      {/* O InteractiveMap recalcula o tamanho ao montar (MapUpdater interno),
          então não é preciso chamar invalidateSize() manualmente aqui. */}
      <div className="agro-overview-mobile-fullscreen-map">
        <InteractiveMap
          tileUrl={ESRI_SATELLITE_TILE_URL}
          tileAttribution={ESRI_SATELLITE_TILE_ATTRIBUTION}
          preferCanvas
          markers={layers.localidades ? locations.map((l) => ({
            locationId: l.id,
            position: [l.latitude, l.longitude] as [number, number],
            label: l.estado
              ? `${removerSufixoUf(l.nome, l.estado)} / ${l.estado}`
              : l.nome,
          })) : []}
        >
          <CanvasHeatLayer points={heatPoints} config={heatConfig} />
          {layers.alertas && locations
            .filter((l): l is AgroOverviewLocation & { risco7d: RiskLevel } => (
              l.risco7d !== null
            ))
            .map((l) => (
              <CircleMarker
                key={`alerta-${l.id}`}
                center={[l.latitude, l.longitude] as [number, number]}
                radius={10}
                pathOptions={{
                  color: '#ffffff', // tema: fixo (desenhado sobre o mapa)
                  weight: 2,
                  fillColor: RISK_COLOR[l.risco7d],
                  fillOpacity: 0.95,
                }}
              />
            ))}
          {layers.limitesEstaduais
            ? <BrazilStatesLayer />
            : <BrazilCountryLayer />}
        </InteractiveMap>
      </div>

      <div className="agro-overview-mobile-fullscreen-scale">
        <div className="agro-overview-mobile-fullscreen-scale-label">
          {`Escala${unidade ? ` (${unidade})` : ''}`}
        </div>
        <div
          className="agro-overview-mobile-fullscreen-scale-bar"
          style={{ gridTemplateColumns: `repeat(${scale.stops.length || 1}, 1fr)` }}
        >
          {scale.stops.map((f) => (
            <span
              key={`${f.min}-${f.max}`}
              className={isLightScaleColor(f.color) ? 'on-light-scale-color' : undefined}
              style={{ backgroundColor: f.color }}
            >
              {scaleStopLabel(f)}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
